"use client";

import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { DiagnosisUnlockSceneBackground } from "../DiagnosisUnlockSceneBackground";
import { DIAGNOSIS_UNLOCK_MODAL_PC_MAX_HEIGHT } from "../diagnosisUnlockModalShell";
import { DiagnosisUnlockPlanPreview } from "./DiagnosisUnlockPlanPreview";
import type {
  DiagnosisUnlockPlanPayload,
  DiagnosisUnlockStepChrome,
} from "../types";
import {
  resolveUnlockStepTopNavAriaLabel,
  resolveUnlockStepTopNavIconName,
} from "./resolveUnlockStepTopNav";

export type DiagnosisUnlockUnlockStepProps = {
  headline: string;
  plan: DiagnosisUnlockPlanPayload;
  /** 数据驱动顶栏 / 底栏 — Figma 39962 / 40004 / 13734 */
  chrome: DiagnosisUnlockStepChrome;
  /** 调用方按会员态传入 — 见 `shouldShowBlurredActionSummary` */
  hideActionSummarySection?: boolean;
  onPrimaryClick: () => void;
  onSecondaryClick?: () => void;
  /**
   * 顶栏 **返回** → 回到 Step2（`previous_step`），不关窗。
   * 顶栏 **关闭** → dismiss（若 chrome 配置 close）。
   */
  onTopNavClick: () => void;
};

const PRIMARY_CTA_CLASS =
  "button_m flex h-11 w-full items-center justify-center rounded-lg bg-background_accent4_overlay100 text-content_inverse_primary transition-opacity hover:opacity-90 active:opacity-80";

const SECONDARY_CTA_CLASS =
  "button_m flex h-8 w-full items-center justify-center text-content_inverse_primary transition-opacity hover:opacity-80 active:opacity-70";

function UnlockTopNavButton({
  topNav,
  onClick,
  testId,
}: {
  topNav: DiagnosisUnlockStepChrome["topNav"];
  onClick: () => void;
  testId: string;
}) {
  const iconName = resolveUnlockStepTopNavIconName(false, topNav);
  const ariaLabel = resolveUnlockStepTopNavAriaLabel(false, topNav);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex size-10 items-center justify-center rounded-[20px] bg-background_solid_neutral_primary"
      data-testid={testId}
      data-openreplay-label={buildOpenReplayLabel(
        "Plan",
        "Unlock",
        topNav.variant === "close" ? "Close" : "Back",
        ariaLabel,
      )}
      aria-label={ariaLabel}
    >
      <Icon name={iconName} size={20} className="text-content_primary" />
    </button>
  );
}

/**
 * Step 3 — Plan 预览 + 解锁 CTA。
 *
 * 顶栏 **返回**（←）仅 `setStep('step2')`，不 resolve 弹窗。
 * 主/次 CTA 文案与 resolve action 均由 `chrome` 配置。
 */
export function DiagnosisUnlockUnlockStep({
  headline,
  plan,
  chrome,
  hideActionSummarySection = false,
  onPrimaryClick,
  onSecondaryClick,
  onTopNavClick,
}: DiagnosisUnlockUnlockStepProps) {
  const [isPc] = useIsPc();
  const topNav = chrome.topNav;
  const showTopNav = topNav.variant !== "none";
  const primaryLabel = chrome.primaryCta.label;
  const secondary = chrome.secondaryCta;
  const showPrimary = chrome.primaryCta.visible !== false;
  const showSecondary = secondary?.visible === true && onSecondaryClick != null;
  const showCtaStack = showPrimary || showSecondary;

  const ctaStack = showCtaStack ? (
    <div className="flex w-full flex-col gap-3">
      {showPrimary ? (
        <button
          type="button"
          className={PRIMARY_CTA_CLASS}
          data-testid="diagnosis-unlock-unlock-primary-cta"
          data-openreplay-label={buildOpenReplayLabel(
            "Plan",
            "Unlock",
            "GoalTap",
            primaryLabel,
          )}
          onClick={onPrimaryClick}
        >
          {primaryLabel}
        </button>
      ) : null}
      {showSecondary ? (
        <button
          type="button"
          className={SECONDARY_CTA_CLASS}
          data-testid="diagnosis-unlock-unlock-secondary-cta"
          data-openreplay-label={buildOpenReplayLabel(
            "Plan",
            "Unlock",
            "MaybeLater",
            secondary.label,
          )}
          onClick={onSecondaryClick}
        >
          {secondary.label}
        </button>
      ) : null}
    </div>
  ) : null;

  if (isPc) {
    return (
      <div
        className="relative flex w-full flex-col overflow-hidden rounded-3xl bg-background_solid_neutral_primary pb-safe pt-6"
        style={{ maxHeight: DIAGNOSIS_UNLOCK_MODAL_PC_MAX_HEIGHT }}
        data-testid="diagnosis-unlock-step-unlock-pc"
      >
        <DiagnosisUnlockSceneBackground variant="pc-modal" />

        {showTopNav ? (
          <div className="relative z-10 flex shrink-0 justify-end px-6 md:px-8">
            <button
              type="button"
              onClick={onTopNavClick}
              className="flex size-6 items-center justify-center"
              data-testid="diagnosis-unlock-unlock-top-nav"
              aria-label={resolveUnlockStepTopNavAriaLabel(true, topNav)}
            >
              <Icon
                name={resolveUnlockStepTopNavIconName(true, topNav)}
                size={24}
                className="text-content_inverse_primary"
              />
            </button>
          </div>
        ) : null}

        <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center gap-10 overflow-y-auto px-6 md:gap-6 md:px-8">
          <h2 className="headline_regular_m w-full text-center leading-8 text-content_inverse_primary">
            {headline}
          </h2>
          <DiagnosisUnlockPlanPreview
            payload={plan}
            hideActionSummarySection={hideActionSummarySection}
          />
        </div>

        <div className="relative z-10 shrink-0 px-6 pt-4 md:px-8 md:pt-10">
          {ctaStack ? <div className="mb-3 md:mb-6">{ctaStack}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full min-h-0 w-full flex-col overflow-hidden pb-safe pt-safe"
      data-testid="diagnosis-unlock-step-unlock-h5"
    >
      <DiagnosisUnlockSceneBackground variant="h5-fullscreen" />

      {showTopNav ? (
        <div className="relative z-10 flex shrink-0 px-4 py-2">
          <UnlockTopNavButton
            topNav={topNav}
            onClick={onTopNavClick}
            testId="diagnosis-unlock-unlock-top-nav"
          />
        </div>
      ) : null}

      <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center gap-10 overflow-y-auto px-6 pt-4">
        <h2 className="headline_regular_m max-w-[345px] text-center leading-8 text-content_inverse_primary">
          {headline}
        </h2>
        <DiagnosisUnlockPlanPreview
          payload={plan}
          hideActionSummarySection={hideActionSummarySection}
        />
      </div>

      <div className="relative z-10 shrink-0 px-4 pt-3">
        {ctaStack ? (
          <div className={cn("mx-auto mb-3 w-full max-w-[361px]")}>
            {ctaStack}
          </div>
        ) : null}
      </div>
    </div>
  );
}
